import { Link, useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { BrandLogo } from '@/components/brand/brand-logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LegalFooter } from '@/components/legal-footer';
import { api, ApiError } from '@/lib/utils';

type PreviewFinding = {
  id: string;
  title: string;
  explanation?: string | null;
  whyItMatters?: string | null;
  evidence: Array<{ pageUrl?: string; snippet?: string; measurement?: string }>;
  severity?: string | null;
};

export function PreviewReportPage() {
  const { code } = useParams({ strict: false }) as { code: string };
  const search = useSearch({ strict: false }) as { remove?: string };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expired, setExpired] = useState(false);
  const [data, setData] = useState<{
    preview: { shortcode: string; targetName?: string | null; expiresAt: string };
    review: {
      domain: string;
      finishedAt?: string | null;
      summaryCounts: { needsAttention?: number; looksGood?: number; couldNotCheck?: number };
    };
    topFindings: PreviewFinding[];
    blurredCount: number;
    signupUrl: string;
  } | null>(null);
  const [removeOpen, setRemoveOpen] = useState(Boolean(search?.remove));
  const [email, setEmail] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [removeMsg, setRemoveMsg] = useState('');

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex,nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    api<NonNullable<typeof data>>(`/api/v1/previews/${code.toLowerCase()}`)
      .then(setData)
      .catch((err) => {
        const e = err as ApiError;
        if (e.status === 410) setExpired(true);
        setError(e.message || 'Report not found');
      })
      .finally(() => setLoading(false));
  }, [code]);

  const requestRemoval = async () => {
    setRemoveMsg('');
    try {
      await api(`/api/v1/previews/${code}/remove-request`, {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      setCodeSent(true);
      setRemoveMsg('If that email matches this report, we sent a confirmation code.');
    } catch (err) {
      setRemoveMsg((err as ApiError).message);
    }
  };

  const confirmRemoval = async () => {
    setRemoveMsg('');
    try {
      await api(`/api/v1/previews/${code}/remove-confirm`, {
        method: 'POST',
        body: JSON.stringify({ email, code: confirmCode }),
      });
      setRemoveMsg('Report removed. This link no longer works.');
      setData(null);
      setError('This report has been removed.');
    } catch (err) {
      setRemoveMsg((err as ApiError).message);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen flex-col bg-bg-app">
        <header className="border-b border-border px-4 py-4">
          <BrandLogo size="sm" to="/" />
        </header>
        <main className="mx-auto max-w-lg flex-1 px-4 py-16 text-center">
          <h1 className="text-xl font-semibold">{expired ? 'This preview expired' : 'Report not available'}</h1>
          <p className="mt-2 text-sm text-text-secondary">{error}</p>
          <Link to="/signup" className="mt-6 inline-flex">
            <Button>Create a free account</Button>
          </Link>
        </main>
        <LegalFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-bg-app">
      <header className="flex items-center justify-between border-b border-border px-4 py-4 sm:px-8">
        <BrandLogo size="sm" to="/" />
        <Link to="/login" className="text-sm font-medium text-text-secondary hover:text-text-primary">
          Log in
        </Link>
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 px-4 py-10">
        <p className="text-xs uppercase tracking-wide text-text-muted">Preview report · public link</p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-text-primary">{data.review.domain}</h1>
        <p className="mt-2 text-[15px] text-text-secondary">
          We took a look at {data.review.domain}
          {data.preview.targetName ? ` (${data.preview.targetName})` : ''}.
          {data.review.finishedAt
            ? ` Reviewed ${new Date(data.review.finishedAt).toLocaleDateString()}.`
            : ''}
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          <span className="rounded-lg bg-danger-subtle px-3 py-1.5 text-sm font-medium text-danger">
            {data.review.summaryCounts.needsAttention ?? data.topFindings.length} need attention
          </span>
          <span className="rounded-lg bg-success-subtle px-3 py-1.5 text-sm font-medium text-success">
            {data.review.summaryCounts.looksGood ?? 0} look good
          </span>
        </div>

        <section className="mt-8 space-y-4">
          <h2 className="text-lg font-semibold">Top findings (verified with evidence)</h2>
          {data.topFindings.map((f) => (
            <article key={f.id} className="rounded-xl border border-border bg-bg-surface p-4">
              <h3 className="font-medium text-text-primary">{f.title}</h3>
              <p className="mt-1 text-sm text-text-secondary">{f.whyItMatters || f.explanation}</p>
              <ul className="mt-3 space-y-1 text-xs text-text-muted">
                {f.evidence.slice(0, 2).map((e, i) => (
                  <li key={i} className="font-mono">
                    {e.snippet || e.measurement || e.pageUrl}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </section>

        {data.blurredCount > 0 && (
          <div className="relative mt-6 overflow-hidden rounded-xl border border-border">
            <div className="space-y-2 p-4 blur-sm select-none" aria-hidden>
              <div className="h-12 rounded-lg bg-bg-subtle" />
              <div className="h-12 rounded-lg bg-bg-subtle" />
              <div className="h-12 rounded-lg bg-bg-subtle" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-bg-surface/70">
              <p className="px-4 text-center text-sm font-medium text-text-primary">
                {data.blurredCount} more findings and ready-to-use fixes
              </p>
            </div>
          </div>
        )}

        <div className="mt-8 rounded-xl border border-brand/20 bg-brand/5 p-6 text-center">
          <p className="text-[15px] font-medium text-text-primary">
            Create a free account to see everything and fix it
          </p>
          <a
            href={data.signupUrl}
            className="mt-4 inline-flex"
            onClick={() => {
              void api(`/api/v1/previews/${code}/cta`, { method: 'POST' }).catch(() => undefined);
            }}
          >
            <Button size="lg">See my full report (free)</Button>
          </a>
        </div>

        <p className="mt-8 text-sm text-text-muted">
          This report only uses public information from your website.{' '}
          <button type="button" className="underline hover:text-text-primary" onClick={() => setRemoveOpen(true)}>
            Remove this report
          </button>
          .
        </p>

        {removeOpen && (
          <div className="mt-4 space-y-3 rounded-xl border border-border p-4">
            <h3 className="font-semibold">Remove this report</h3>
            <p className="text-sm text-text-secondary">
              Confirm with the email associated with this report. Removal is immediate after the code check.
            </p>
            <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            {!codeSent ? (
              <Button size="sm" onClick={requestRemoval}>
                Send confirmation code
              </Button>
            ) : (
              <>
                <Input
                  label="Confirmation code"
                  value={confirmCode}
                  onChange={(e) => setConfirmCode(e.target.value)}
                />
                <Button size="sm" onClick={confirmRemoval}>
                  Confirm removal
                </Button>
              </>
            )}
            {removeMsg && <p className="text-sm text-text-secondary">{removeMsg}</p>}
          </div>
        )}
      </main>
      <LegalFooter />
    </div>
  );
}
